import React from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import { FiArrowRight, FiCode, FiZap, FiTrendingUp, FiUsers, FiLock, FiServer, FiShield } from 'react-icons/fi';
import { internalUrl, blogArticleUrl, BLOG_SLUG_FREE_WEBSITE_UK } from './siteConfig';

const HeroSection = () => {
  const stats = [
    { icon: FiCode, number: '5+', label: 'Projects delivered' },
    { icon: FiUsers, number: '5+', label: 'Client partnerships' },
    { icon: FiZap, number: '99%', label: 'Uptime target' },
    { icon: FiTrendingUp, number: 'Mon–Fri', label: 'Support window' }
  ];

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden border-b border-slate-200/60">
      <div className="absolute inset-0 bg-[#fafaf9]" aria-hidden />
      <div
        className="absolute inset-0 opacity-[0.35] bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(15,23,42,0.08),transparent)]"
        aria-hidden
      />
      <m.div
        className="absolute -top-32 right-0 h-96 w-96 rounded-full bg-slate-200/40 blur-3xl hidden lg:block"
        animate={{ opacity: [0.4, 0.55, 0.4] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        aria-hidden
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <m.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <m.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 rounded-full border border-slate-200/80 bg-white/80 text-slate-600 text-sm font-medium mb-6 shadow-sm backdrop-blur-sm"
            >
              <FiZap className="mr-2 text-slate-700" aria-hidden />
              South Devon · South West · United Kingdom
            </m.div>

            <m.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28 }}
              className="mb-6 w-full max-w-md mx-auto lg:mx-0 lg:hidden"
            >
              <a
                href={internalUrl('contact')}
                className="inline-flex w-full min-h-[48px] items-center justify-center px-6 py-3.5 rounded-xl bg-indigo-600 text-white font-semibold shadow-md hover:bg-indigo-700 transition-colors"
              >
                Request a quote
              </a>
            </m.div>

            <m.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold mb-6 leading-[1.15] tracking-tight"
            >
              <span className="block text-slate-900">
                WebCodeCraft — professional web design in Devon &amp; the UK
              </span>{' '}
              <span className="block gradient-text mt-2">
                No upfront build cost — pay monthly, free design &amp; build · South West &amp; UK-wide
              </span>
            </m.h1>

            <m.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-lg sm:text-xl text-slate-600 mb-6 max-w-2xl leading-relaxed mx-auto lg:mx-0"
            >
              Professional web design in Devon and the UK with no upfront build cost: pay monthly
              for Plymouth, across Devon, Cornwall, and the wider South West—or anywhere nationwide.
              We are a South Devon web development team working with founders and established
              companies across the United Kingdom. You get bespoke website design, technical
              SEO foundations, SSL, UK-minded hosting options, and ongoing care—without a large
              upfront build fee because your monthly plan covers delivery and operations working
              together. Startups, small businesses, and SMEs in Devon use us when they want an affordable
              custom build that still feels bespoke—not a one-size-fits-all template—paired with clear
              security expectations and plans they can budget month to month.
            </m.p>

            <ul className="flex flex-wrap justify-center lg:justify-start gap-x-5 gap-y-2 text-sm text-slate-600 mb-6 max-w-2xl mx-auto lg:mx-0 list-none p-0">
              <li className="flex items-center gap-2">
                <FiLock className="text-slate-700 shrink-0" aria-hidden />
                <span>SSL certificate (HTTPS) on every plan</span>
              </li>
              <li className="flex items-center gap-2">
                <FiServer className="text-slate-700 shrink-0" aria-hidden />
                <span>UK hosting options</span>
              </li>
              <li className="flex items-center gap-2">
                <FiShield className="text-slate-700 shrink-0" aria-hidden />
                <span>Backups &amp; security-minded care</span>
              </li>
            </ul>

            <p className="text-sm text-slate-600 mb-3 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              <Link
                to="/locations/plymouth"
                className="text-slate-900 font-medium underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900"
              >
                Web Design Plymouth
              </Link>
              {' · '}
              <Link
                to="/locations/ipswich"
                className="text-slate-900 font-medium underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900"
              >
                Web Design Ipswich
              </Link>
              {' '}
              — dedicated location pages for monthly-first builds.
            </p>

            <p className="text-sm text-slate-500 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              Jump to{' '}
              <a href={internalUrl('services')} className="text-slate-900 font-medium underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900">
                website packages &amp; pricing
              </a>
              ,{' '}
              <a href={internalUrl('portfolio')} className="text-slate-900 font-medium underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900">
                portfolio examples
              </a>
              ,{' '}
              <a href={internalUrl('about')} className="text-slate-900 font-medium underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900">
                about WebCodeCraft
              </a>
              , the{' '}
              <a href={internalUrl('faq')} className="text-slate-900 font-medium underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900">
                FAQ
              </a>
              , our{' '}
              <a href={blogArticleUrl(BLOG_SLUG_FREE_WEBSITE_UK)} className="text-slate-900 font-medium underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900">
                UK website builders comparison guide
              </a>
              , the{' '}
              <a href={internalUrl('tax-calculator')} className="text-slate-900 font-medium underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900">
                website tax calculator
              </a>
              , or{' '}
              <a href={internalUrl('contact')} className="text-slate-900 font-medium underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900">
                start a project
              </a>
              .
            </p>

            <m.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <m.button
                className="btn btn-primary btn-large group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Start a project
                <FiArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </m.button>

              <m.button
                className="btn btn-secondary btn-large"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })}
              >
                View work
              </m.button>
            </m.div>
          </m.div>

          <m.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative bg-white rounded-xl p-4 sm:p-6 md:p-8 shadow-2xl border border-slate-200/80 ring-1 ring-slate-900/5">
              <div className="bg-slate-950 rounded-lg p-4 mb-6">
                <div className="flex items-center mb-3">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500/90 rounded-full" />
                    <div className="w-3 h-3 bg-amber-400/90 rounded-full" />
                    <div className="w-3 h-3 bg-emerald-500/90 rounded-full" />
                  </div>
                  <span className="text-slate-400 text-xs font-mono ml-4 tracking-wide">App.tsx</span>
                </div>
                <div className="text-emerald-400/95 text-sm font-mono leading-relaxed">
                  <div>
                    <span className="text-slate-400">{'// Production-ready build'}</span>
                  </div>
                  <div className="mt-2">
                    &lt;<span className="text-sky-400">section</span>{' '}
                    <span className="text-amber-300">className</span>=
                    <span className="text-orange-300">&quot;hero&quot;</span> /&gt;
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {stats.map((stat, index) => (
                  <m.div
                    key={stat.label}
                    className="text-center p-3 sm:p-4 bg-slate-50 rounded-lg border border-slate-100"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <stat.icon className="text-xl sm:text-2xl text-slate-700 mx-auto mb-1 sm:mb-2" />
                    <div className="text-lg sm:text-2xl font-semibold text-slate-900 font-display">{stat.number}</div>
                    <div className="text-xs sm:text-sm text-slate-600">{stat.label}</div>
                  </m.div>
                ))}
              </div>
            </div>
          </m.div>
        </div>

        <m.div
          className="relative w-full max-w-4xl mx-auto mt-14 lg:mt-20 pt-10 lg:pt-14 border-t border-slate-200/80"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.55 }}
        >
          <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-semibold text-slate-900 mb-5 text-center lg:text-left">
            Professional web design &amp; custom development — UK and South West
          </h2>
          <div className="space-y-4 text-slate-600 leading-relaxed text-sm sm:text-base">
            <p>
              WebCodeCraft focuses on custom web development for organisations that outgrow
              generic templates. Whether you need a marketing site, a small e-commerce
              catalogue, a booking-led workflow, or clearer service pages for local search in
              Devon, the wider South West, and beyond, we structure content and metadata so search engines and people
              both understand what you offer. Technical SEO—clean URLs, sensible headings,
              performance-conscious assets, and accessible components—is part of the baseline,
              not a paid mystery add-on.
            </p>
            <p>
              Our free website design and build model means you do not pay a separate invoice
              for the initial design and construction: you choose a hosting and care tier so
              the site stays fast, backed up, and patched. That pairing keeps your total cost of
              ownership predictable while we stay accountable for the stack. If you are
              comparing agencies, read our{' '}
              <a
                href={blogArticleUrl(BLOG_SLUG_FREE_WEBSITE_UK)}
                className="text-slate-900 font-medium underline underline-offset-2 decoration-slate-300 hover:decoration-slate-900"
              >
                guide on UK website builders vs custom development
              </a>
              , then review{' '}
              <a
                href={internalUrl('services')}
                className="text-slate-900 font-medium underline underline-offset-2 decoration-slate-300 hover:decoration-slate-900"
              >
                what each package includes
              </a>{' '}
              and real outcomes in the{' '}
              <a
                href={internalUrl('portfolio')}
                className="text-slate-900 font-medium underline underline-offset-2 decoration-slate-300 hover:decoration-slate-900"
              >
                portfolio
              </a>
              , or see a{' '}
              <a
                href={internalUrl('client-review')}
                className="text-slate-900 font-medium underline underline-offset-2 decoration-slate-300 hover:decoration-slate-900"
              >
                recent client review
              </a>
              .
            </p>
            <p>
              Based in South Devon in the South West, we work remotely with clients across England, Scotland,
              Wales, and Northern Ireland. Use the{' '}
              <a
                href={internalUrl('contact')}
                className="text-slate-900 font-medium underline underline-offset-2 decoration-slate-300 hover:decoration-slate-900"
              >
                contact section
              </a>{' '}
              to outline your goals, or open the{' '}
              <a
                href={internalUrl('faq')}
                className="text-slate-900 font-medium underline underline-offset-2 decoration-slate-300 hover:decoration-slate-900"
              >
                FAQ
              </a>{' '}
              for timelines, ownership, and hosting. For sole traders weighing tax treatment of
              software and sites, the{' '}
              <a
                href={internalUrl('tax-calculator')}
                className="text-slate-900 font-medium underline underline-offset-2 decoration-slate-300 hover:decoration-slate-900"
              >
                website purchases tax calculator
              </a>{' '}
                may help alongside professional advice.
            </p>
            <h3 className="font-display text-lg sm:text-xl font-semibold text-slate-900 pt-2">
              Affordable custom websites for Devon startups, small businesses, and SMEs
            </h3>
            <p>
              If you run a startup, trade, shop, or professional practice, your website should state what you do,
              who you serve, and how to get in touch without drowning visitors in clutter. We plan navigation,
              page structure, and on-page guidance so people and search engines read the same clear story. You
              pick a hosting and care tier that matches how much help you want after launch; larger content or
              feature work is quoted separately so you always know what the monthly fee includes.
            </p>
            <p>
              Security and reliability are part of the baseline: HTTPS, disciplined hosting, backups, and updates
              on sensible maintenance cycles. Optional SEO and security retainers add deeper reporting,
              monitoring, and optimisation, and can waive standard hosting fees when combined with the right
              tier—see services for the latest options. Whether you need a lean one-page presence or a multi-page
              brochure with booking or light e-commerce, we aim for predictable cost of ownership for teams that
              cannot absorb surprise invoices.
            </p>
            <p>
              From Exeter to coastal towns and across Devon, we follow one clear process: agree goals, pick a
              package, design and build, then launch with documentation so you know how to request updates.
              Monthly service keeps patching, backups, and agreed support on our roadmap rather than yours alone.
            </p>
          </div>
        </m.div>

        <m.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 hidden md:block"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
        >
          <m.div
            className="w-6 h-10 border-2 border-slate-300 rounded-full flex justify-center"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-1 h-3 bg-slate-400 rounded-full mt-2" />
          </m.div>
        </m.div>
      </div>
    </section>
  );
};

export default HeroSection;
